import { useNavigate, useRevalidator } from "react-router";
import { Box, Container, Button, TextField, FormLabel } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useCreateProductLine } from "src/api/client-api/productLines/useCreateProductLine";
import { ProductLineFormService, ProductLineGridForm, ProductLineGridFormZodSchema } from "./ProductLineForm";
import { FormProductLineSelector } from "src/components/Selector/ProductLineSelector/FormProductLineSelector";
import { URLHandler } from "src/services/URLHandler";
import { ProductLine } from "src/api/client-api/productLines/useProductLines";

type ProductLineCreateProps = {
    productLine?: ProductLine;
};

export const ProductLineCreate = (props: ProductLineCreateProps) => {
    const { productLine } = props;
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const { t } = useTranslation();

    const createProductLine = useCreateProductLine();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ProductLineGridForm>({
        mode: "onChange",
        resolver: zodResolver(ProductLineGridFormZodSchema as any),
        defaultValues: ProductLineFormService.getProductLineToForm(productLine),
    });

    const onSubmitHandler = (values: ProductLineGridForm) => {
        const productLineWrite = ProductLineFormService.getFormToProductLineWrite(values);

        createProductLine.mutate(
            { productLine: productLineWrite },
            {
                onSuccess: () => {
                    revalidator.revalidate();
                    navigate(URLHandler.settings.productLine.list());
                },
                onError: (error) => {},
            },
        );
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <h1>
                {productLine ?
                    t("edit_product_line", "Edit Product Line")
                :   t("create_product_line", "Create Product Line")}
            </h1>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
                <Controller name="id" control={control} render={({ field }) => <input type="hidden" {...field} />} />

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "1rem",
                    }}
                >
                    <FormLabel>{t("nom_de_la_gamme")}</FormLabel>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                label={t("nom_de_la_gamme")}
                                type="text"
                                variant="outlined"
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                                inputRef={ref}
                                {...field}
                            />
                        )}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "1rem",
                    }}
                >
                    <FormLabel>{t("parent_product_line", "Parent Product Line")}</FormLabel>
                    <Controller
                        name="parent"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <FormProductLineSelector
                                value={value || ""}
                                onChange={onChange}
                                excludeId={productLine?.id}
                            />
                        )}
                    />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Button
                        sx={(theme) => ({
                            marginTop: "1rem",
                            width: "10rem",
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                color: "white !important",
                            },
                            "&:disabled": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                opacity: 0.36,
                                color: "white !important",
                            },
                        })}
                        variant="contained"
                        type="submit"
                    >
                        {t("enregistrer")}
                    </Button>
                </Box>
            </form>
        </Container>
    );
};
